import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EMPTY_VALUE, formatDateTime } from './formatting';

import Conversation from './conversation';
import ReportTypeChip from './report_type_chip';
import ReviewActions from './review_actions';

import styles from './event_drawer.module.css';

const Field = ({ label, children }) => (
  <div className={styles.field}>
    <span className={styles.field_label}>{label}</span>
    <span className={styles.field_value}>{children}</span>
  </div>
);

// English is the primary reading, so the toggle starts there and the original is the alternative.
// A conversation held in English has nothing to toggle between and gets no toggle at all.
const TranslationToggle = ({ translation, showEnglish, onChange }) => {
  if (!translation.english_available) {
    return null;
  }

  return (
    <div aria-label='Conversation language' className={styles.toggle} role='group'>
      <button
        aria-pressed={showEnglish}
        className={showEnglish ? `${styles.toggle_option} ${styles.toggle_selected}` : styles.toggle_option}
        onClick={() => onChange(true)}
        type='button'
      >
        English translation
      </button>
      <button
        aria-pressed={!showEnglish}
        className={showEnglish ? styles.toggle_option : `${styles.toggle_option} ${styles.toggle_selected}`}
        onClick={() => onChange(false)}
        type='button'
      >
        Original{translation.original_language ? ` (${translation.original_language})` : ''}
      </button>
    </div>
  );
};

// Said explicitly, so a reader can tell a conversation we could not translate apart from one that
// was held in English in the first place.
const TranslationNotice = ({ translation }) => {
  if (translation.originally_english || translation.english_available) {
    return null;
  }

  return <p className={styles.notice}>An English translation of this conversation is not available.</p>;
};

const DrawerBody = ({ event, showEnglish, onLanguageChange }) => (
  <>
    <div className={styles.summary}>
      <span className={styles.types}>
        {(event.report_types?.length ? event.report_types : [event.report_type].filter(Boolean)).map((reportType) => (
          <ReportTypeChip key={reportType} reportType={reportType} />
        ))}
      </span>
      <span className={styles.source}>{event.source}</span>
    </div>

    <div className={styles.fields}>
      <Field label='Site Name'>{event.site_name || EMPTY_VALUE}</Field>
      <Field label='Site ID'>{event.site_id || EMPTY_VALUE}</Field>
      <Field label='Subject ID'>{event.subject_id || EMPTY_VALUE}</Field>
      <Field label='Latest Participant Status'>{event.latest_participant_status}</Field>
      <Field label='Report Date/Time'>{formatDateTime(event.reported_at)}</Field>
      <Field label='Site Notification Date/Time'>{formatDateTime(event.site_notified_at)}</Field>
    </div>

    <section className={styles.section}>
      <h3 className={styles.section_title}>Keyword or Mentor Report</h3>
      <p className={styles.snippet}>
        {(showEnglish && event.keyword_or_mentor_report_english) || event.keyword_or_mentor_report || EMPTY_VALUE}
      </p>
    </section>

    <section className={styles.section}>
      <h3 className={styles.section_title}>Full Text of Reported Conversation</h3>
      <TranslationToggle onChange={onLanguageChange} showEnglish={showEnglish} translation={event.translation} />
      <TranslationNotice translation={event.translation} />
      <Conversation messages={event.full_conversation} showEnglish={showEnglish} />
      <p className={styles.redaction_note}>
        Shown verbatim with identifying information redacted. Captured conversations are source data and cannot be
        edited.
      </p>
    </section>

    <section className={styles.section}>
      <h3 className={styles.section_title}>Reporting Notes</h3>
      {event.reporting_notes ? (
        <p className={styles.notes}>{event.reporting_notes}</p>
      ) : (
        <p className={styles.notes_empty}>PatientPartner has not recorded any notes on this event.</p>
      )}
    </section>
  </>
);

export default function EventDrawer({
  eventId,
  event,
  loading,
  error,
  saving,
  savingError,
  onClose,
  onToggleReviewed,
  onMarkUnread,
}) {
  const [showEnglish, setShowEnglish] = useState(true);
  const closeButtonRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const opener = document.activeElement;
    const drawer = drawerRef.current;
    closeButtonRef.current?.focus();

    const onKeyDown = (keyEvent) => {
      if (keyEvent.key === 'Escape') {
        keyEvent.preventDefault();
        onClose();
        return;
      }

      if (keyEvent.key !== 'Tab' || !drawer) return;

      const focusable = Array.from(
        drawer.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.disabled);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (keyEvent.shiftKey && document.activeElement === first) {
        keyEvent.preventDefault();
        last.focus();
      } else if (!keyEvent.shiftKey && document.activeElement === last) {
        keyEvent.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [onClose]);

  // English is primary for every event, so the choice resets rather than carrying the previous
  // event's language over to the next one opened.
  useEffect(() => {
    setShowEnglish(true);
  }, [eventId]);

  // The clinical shell is position:fixed with overflow hidden, which clips this panel.
  // Portaling it onto the body lets it cover the viewport below the session bar.
  return createPortal(
    <>
      <div className={styles.scrim} onClick={onClose} />
      <aside
        aria-labelledby='reportable-event-drawer-title'
        aria-modal='true'
        className={styles.drawer}
        ref={drawerRef}
        role='dialog'
      >
        <header className={styles.header}>
          <h2 className={styles.title} id='reportable-event-drawer-title'>
            Report {eventId}
          </h2>
          <button aria-label='Close' className={styles.close} onClick={onClose} ref={closeButtonRef} type='button'>
            <svg aria-hidden='true' viewBox='0 0 16 16'>
              <path d='M4 4L12 12M12 4L4 12' fill='none' stroke='#ffffff' strokeLinecap='round' strokeWidth='1.5' />
            </svg>
          </button>
        </header>

        <div className={styles.content}>
          {loading && <p className={styles.status}>Loading event…</p>}
          {!loading && error && <p className={styles.error}>{error}</p>}
          {!loading && !error && event && (
            <DrawerBody event={event} onLanguageChange={setShowEnglish} showEnglish={showEnglish} />
          )}
        </div>

        {!loading && !error && event && (
          <ReviewActions
            error={savingError}
            event={event}
            onMarkUnread={onMarkUnread}
            onToggleReviewed={onToggleReviewed}
            saving={saving}
          />
        )}
      </aside>
    </>,
    document.body
  );
}
